import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Space, Text, Button, InputField } from '@cashfree-intl/coherent';

import FormikComponent from 'components/Formik';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';
import { COMPLETED, PAYTM_DASHBOARD_LINK } from '../../constants';
import validationSchema from './validation';

function PaytmForm({
  data,
  addPaytmTestConfig,
  addPaytmProdConfig,
  isEditMode,
  resetEditMode,
  closeModal,
  resetShowPaytmForm,
}) {
  const { paytmTestConfig, paytmProdConfig } = data;
  const { merchantPaytmId, merchantPaytmKey, stage } = paytmTestConfig;

  const initialValues = {
    merchantPaytmId:
      stage === COMPLETED ? paytmProdConfig.merchantPaytmId : merchantPaytmId,
    merchantPaytmKey:
      stage === COMPLETED ? paytmProdConfig.merchantPaytmKey : merchantPaytmKey,
    merchantPaytmWebsiteUrl: stage === COMPLETED ? 'DEFAULT' : 'WEBSTAGING',
    merchantPaytmChannelId: 'WEB',
    merchantPaytmIndustryType: 'Retail',
  };

  const onSubmit = (values, actions) => {
    if (stage !== COMPLETED) {
      if (isEditMode) {
        const payload = {
          id: paytmTestConfig.id,
          merchantPaytmId: values.merchantPaytmId,
          merchantPaytmKey: values.merchantPaytmKey,
        };
        addPaytmTestConfig({
          payload,
          actions,
          isEditMode,
          cbShowForm: resetShowPaytmForm,
          cbEditMode: resetEditMode,
        });
      } else {
        addPaytmTestConfig({
          payload: values,
          actions,
          cbShowForm: resetShowPaytmForm,
        });
      }
      Analytics.track(EVENTS.PAYMENT_METHODS.ADD_PAYTM_TEST_CONFIG);
    } else {
      const payload = {
        merchantPaytmId: values.merchantPaytmId,
        merchantPaytmKey: values.merchantPaytmKey,
        merchantPaytmWebsiteUrl: values.merchantPaytmWebsiteUrl,
      };
      addPaytmProdConfig({
        payload,
        actions,
        cb: resetShowPaytmForm,
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.ADD_PAYTM_PROD_CONFIG);
    }
  };

  return (
    <FormikComponent
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({
        values = {},
        handleFieldBlur,
        handleFieldChange,
        handleSubmit,
        errors,
        touched,
        ...formikProps
      }) => {
        const disabledField =
          !formikProps.isValidating && formikProps.isSubmitting;

        return (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <Space>
              <Space className="content-left" direction="column">
                <Text className="active" variant="b14" color="body" strong>
                  {stage === COMPLETED ? 'Production' : 'Test'} API Credentials
                </Text>
                <Text variant="b14" color="bodyLight" strong>
                  Test Transactions
                </Text>
              </Space>
              <Space direction="column">
                {stage === COMPLETED ? (
                  <Text className="description" variant="b14" color="bodyLight">
                    Your production API credentials are available in the
                    developer settings section of your{' '}
                    {
                      <a href={PAYTM_DASHBOARD_LINK} target="_blank">
                        Paytm dashboard
                      </a>
                    }
                    . Please enter them and proceed to do a transaction to
                    validate them.
                  </Text>
                ) : (
                  <Text className="description" variant="b14" color="bodyLight">
                    Enter your test API details below. They can be found in the
                    developer settings section of your PayTM dashboard, access
                    it by clicking{' '}
                    {
                      <a href={PAYTM_DASHBOARD_LINK} target="_blank">
                        here
                      </a>
                    }
                    .
                  </Text>
                )}
                <div className="ml-3 mb-3">
                  <InputField
                    fluid
                    id="merchantPaytmId"
                    name="merchantPaytmId"
                    label={`${
                      stage === COMPLETED ? 'Production' : 'Test'
                    } Merchant ID`}
                    value={values.merchantPaytmId ? values.merchantPaytmId : ''}
                    errors={errors}
                    touched={touched}
                    placeholder=""
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    disabled={disabledField}
                  />
                </div>
                <div className="ml-3 mb-3">
                  <InputField
                    fluid
                    id="merchantPaytmKey"
                    name="merchantPaytmKey"
                    label={`${
                      stage === COMPLETED ? 'Production' : 'Test'
                    } Merchant Key`}
                    value={
                      values.merchantPaytmKey ? values.merchantPaytmKey : ''
                    }
                    errors={errors}
                    touched={touched}
                    placeholder=""
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    disabled={disabledField}
                  />
                </div>
              </Space>
            </Space>
            <Space justifyContent="flex-end" className="cta-wrapper">
              <Button
                link
                onClick={closeModal}
                disabled={disabledField}
                data-event-name="Form_PaytmForm_Button_Cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={disabledField}
                disabled={!formikProps.isValid || disabledField}
                primary
                data-event-name="Primary_Button_Next"
              >
                Next
              </Button>
            </Space>
          </form>
        );
      }}
    </FormikComponent>
  );
}

PaytmForm.propTypes = {
  data: PropTypes.object.isRequired,
  addPaytmTestConfig: PropTypes.func.isRequired,
  addPaytmProdConfig: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  resetEditMode: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  resetShowPaytmForm: PropTypes.func.isRequired,
};

export default memo(PaytmForm);
